/**
 * A free-text search that lives in the URL without thrashing it (TMM-B6, UI-009): the field is a
 * controlled local value, so typing never waits on the router and never loses focus, and the
 * committed value reaches the URL (with `replace`, so no history entry per keystroke) once the
 * person pauses. A change that arrives from the URL (Back, a link, Clear filters) replaces what
 * the field shows and drops a commit still waiting.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

export function useDebouncedText(
  committed: string,
  commit: (value: string) => void,
  delayMs = 300,
): [string, (value: string) => void] {
  const [text, setText] = useState(committed);
  const timer = useRef<number | null>(null);
  // The last value this field committed, or took from the URL: a `committed` that equals it is
  // our own commit landing, which must not overwrite what has been typed since.
  const own = useRef(committed);
  const commitRef = useRef(commit);
  useEffect(() => {
    commitRef.current = commit;
  });

  const clearTimer = useCallback(() => {
    if (timer.current !== null) {
      window.clearTimeout(timer.current);
      timer.current = null;
    }
  }, []);

  useEffect(() => {
    if (committed === own.current) return;
    own.current = committed;
    clearTimer();
    setText(committed);
  }, [committed, clearTimer]);

  const change = useCallback(
    (value: string) => {
      setText(value);
      clearTimer();
      timer.current = window.setTimeout(() => {
        timer.current = null;
        own.current = value;
        commitRef.current(value);
      }, delayMs);
    },
    [clearTimer, delayMs],
  );

  // A commit still waiting when the field goes away must not navigate from a page that is gone.
  useEffect(() => clearTimer, [clearTimer]);

  return [text, change];
}
